import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import './sass/shape-renderer.scss';

const DEFAULT_STYLE = {
  strokeColor: 'rgba(255,0,0,0.4)',
  lineWidth: 2,
};

const drawPolygon = (ctx, points, style = DEFAULT_STYLE) => {
  if (!points || points.length < 2) return;

  ctx.strokeStyle = style.strokeColor || DEFAULT_STYLE.strokeColor;
  ctx.lineWidth = style.lineWidth || DEFAULT_STYLE.lineWidth;

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }

  ctx.closePath();

  if (style.strokeColor) ctx.stroke();
};

const calculateInformationBox = (ctx, points, information, canvasWidth, canvasHeight, style = DEFAULT_STYLE) => {
  if (!information || information.length === 0) return null;

  // Calculate scale factor
  const minDimension = Math.min(canvasWidth, canvasHeight);
  const scaleFactor = Math.max(0.5, Math.min(minDimension / 800, 2));

  // Find the bottom-right area of the shape
  const maxX = Math.max(...points.map((p) => p.x));
  const maxY = Math.max(...points.map((p) => p.y));

  // Calculate the centroid of the shape
  const centroidX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
  const centroidY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

  // Scale dimensions (reduced for smaller box)
  const padding = Math.round(6 * scaleFactor);
  const lineHeight = Math.round(14 * scaleFactor);
  const fontSize = Math.round(10 * scaleFactor);

  ctx.font = `${fontSize}px Arial`;
  ctx.textBaseline = 'top';

  // Calculate information box dimensions
  const maxTextWidth = Math.max(
    ...information.map((text) => ctx.measureText(text).width)
  );
  const boxWidth = maxTextWidth + (padding * 2);
  const boxHeight = ((information.length - 1) * lineHeight) + fontSize + (padding * 2);

  // Find a random point in the bottom-right quadrant of the polygon
  // Use bottom-right area (from centroid to max X/Y)
  const bottomRightPoints = points.filter((p) => p.x >= centroidX && p.y >= centroidY);

  let boxX;
  let boxY;
  if (bottomRightPoints.length > 0) {
    // Pick a random point from bottom-right area
    const randomPoint = bottomRightPoints[Math.floor(Math.random() * bottomRightPoints.length)];
    boxX = randomPoint.x;
    boxY = randomPoint.y;
  } else {
    // Fallback to max corner if no points in bottom-right
    boxX = maxX;
    boxY = maxY;
  }

  // Ensure the box stays within canvas bounds with proper padding
  const finalX = Math.min(boxX, canvasWidth - boxWidth - Math.round(5 * scaleFactor));
  const finalY = Math.min(boxY, canvasHeight - boxHeight - Math.round(5 * scaleFactor));

  return {
    x: finalX,
    y: finalY,
    width: boxWidth,
    height: boxHeight,
    centroidX,
    centroidY,
    information,
    padding,
    lineHeight,
    fontSize,
    scaleFactor,
    strokeColor: style.strokeColor || DEFAULT_STYLE.strokeColor
  };
};

const resolveOverlappingBoxes = (informationBoxes) => {
  const boxes = [...informationBoxes];

  // Simple overlap resolution - move overlapping boxes
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const box1 = boxes[i];
      const box2 = boxes[j];

      // Check for overlap
      if (box1.x < box2.x + box2.width
          && box1.x + box1.width > box2.x
          && box1.y < box2.y + box2.height
          && box1.y + box1.height > box2.y) {
        // Move box2 to avoid overlap
        const overlapX = Math.min(box1.x + box1.width - box2.x, box2.x + box2.width - box1.x);
        const overlapY = Math.min(box1.y + box1.height - box2.y, box2.y + box2.height - box1.y);

        // Move in the direction with less overlap
        if (overlapX < overlapY) {
          // Move horizontally
          if (box1.x < box2.x) {
            box2.x = box1.x + box1.width + Math.round(5 * box2.scaleFactor);
          } else {
            box2.x = box1.x - box2.width - Math.round(5 * box2.scaleFactor);
          }
        } else if (box1.y < box2.y) {
          // Move vertically
          box2.y = box1.y + box1.height + Math.round(5 * box2.scaleFactor);
        } else {
          box2.y = box1.y - box2.height - Math.round(5 * box2.scaleFactor);
        }

        // Ensure box2 stays within canvas bounds
        box2.x = Math.max(0, Math.min(box2.x, 800 - box2.width)); // Assuming max canvas width
        box2.y = Math.max(0, Math.min(box2.y, 600 - box2.height)); // Assuming max canvas height
      }
    }
  }

  return boxes;
};

const drawInformationBox = (ctx, box) => {
  const { x, y, width, height, information, padding, lineHeight, scaleFactor, strokeColor } = box;

  // Draw information background (white like legend)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillRect(x, y, width, height);

  // Draw information border (using label's stroke color)
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = Math.max(1, Math.round(1 * scaleFactor));
  ctx.strokeRect(x, y, width, height);

  // Draw information text (black like legend)
  ctx.fillStyle = 'black';
  information.forEach((text, index) => {
    const textX = x + padding;
    // Use lineHeight spacing for all but the last element
    const textY = y + padding + (index * lineHeight);
    ctx.fillText(text, textX, textY);
  });
};

const drawLegend = (ctx, uniqueLabels, canvasWidth, canvasHeight) => {
  if (!uniqueLabels || uniqueLabels.length === 0) return;

  // Calculate scale factor based on image resolution
  // Use a minimum scale of 0.5 and maximum of 2 for reasonable legend sizes
  const minDimension = Math.min(canvasWidth, canvasHeight);
  const scaleFactor = Math.max(0.5, Math.min(minDimension / 800, 2)); // Base scale on 800px reference

  // Scale all dimensions (reduced base values for smaller legend)
  const legendPadding = Math.round(8 * scaleFactor);
  const lineLength = Math.round(16 * scaleFactor);
  const lineSpacing = Math.round(20 * scaleFactor);
  const textOffset = Math.round(6 * scaleFactor);
  const fontSize = Math.round(12 * scaleFactor);

  ctx.font = `${fontSize}px Arial`;
  ctx.textBaseline = 'middle';

  // Calculate legend background dimensions
  const maxTextWidth = Math.max(
    ...uniqueLabels.map((item) => ctx.measureText(item.label).width)
  );
  const legendWidth = lineLength + textOffset + maxTextWidth + (legendPadding * 2);
  const legendHeight = (uniqueLabels.length * lineSpacing) + (legendPadding * 2);

  // Draw legend background
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillRect(legendPadding, legendPadding, legendWidth, legendHeight);

  // Draw legend border
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.lineWidth = Math.max(1, Math.round(1 * scaleFactor));
  ctx.strokeRect(legendPadding, legendPadding, legendWidth, legendHeight);

  // Draw legend items
  uniqueLabels.forEach((item, index) => {
    const y = legendPadding * 2 + (index * lineSpacing) + (lineSpacing / 2);
    const lineStartX = legendPadding * 2;
    const lineEndX = lineStartX + lineLength;
    const textX = lineEndX + textOffset;

    // Draw colored line
    ctx.strokeStyle = item.strokeColor;
    ctx.lineWidth = Math.max(item.lineWidth || DEFAULT_STYLE.lineWidth, Math.round(2 * scaleFactor));
    ctx.beginPath();
    ctx.moveTo(lineStartX, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();

    // Draw label text
    ctx.fillStyle = 'black';
    ctx.fillText(item.label, textX, y);
  });
};

const ShapeRenderer = ({
  imageSrc,
  shapes = [],
  canvasStyle = {},
  className = '',
  onCanvasReady,
  ...props
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');

    const handleImageLoad = () => {
      // Set canvas dimensions to match image
      canvas.width = image.width;
      canvas.height = image.height;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw image
      ctx.drawImage(image, 0, 0);

      // Draw all polygons first
      shapes.forEach((shape) => {
        const { points, style } = shape;
        drawPolygon(ctx, points, style);
      });

      // Collect all information boxes
      const informationBoxes = [];
      shapes.forEach((shape) => {
        const { points, information, style } = shape;
        if (information && information.length > 0) {
          const box = calculateInformationBox(ctx, points, information, canvas.width, canvas.height, style);
          if (box) {
            informationBoxes.push(box);
          }
        }
      });

      // Resolve overlapping boxes
      const resolvedBoxes = resolveOverlappingBoxes(informationBoxes);

      // Draw all information boxes
      resolvedBoxes.forEach((box) => {
        drawInformationBox(ctx, box);
      });

      // Extract unique labels for legend
      const uniqueLabelsMap = new Map();
      shapes.forEach((shape) => {
        const { label, style } = shape;
        if (label && !uniqueLabelsMap.has(label)) {
          uniqueLabelsMap.set(label, {
            label,
            strokeColor: style?.strokeColor || DEFAULT_STYLE.strokeColor,
            lineWidth: style?.lineWidth || DEFAULT_STYLE.lineWidth,
          });
        }
      });

      // Draw legend if there are labeled shapes
      if (uniqueLabelsMap.size > 0) {
        drawLegend(ctx, Array.from(uniqueLabelsMap.values()), canvas.width, canvas.height);
      }

      // Callback when canvas is ready
      if (onCanvasReady) {
        onCanvasReady(canvas, ctx);
      }
    };

    // Check if image is already loaded
    if (image.complete && image.naturalHeight !== 0) {
      handleImageLoad();
    } else {
      image.onload = handleImageLoad;
    }
  }, [imageSrc, shapes, onCanvasReady]);

  // If no shapes to render, just show the image
  if (!shapes || shapes.length === 0) {
    return <img src={imageSrc} alt="Shape renderer" className={className} {...props} />;
  }

  return (
    <div className={`shape-renderer-wrapper ${className}`}>
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Shape renderer base"
        style={{ display: 'none' }}
      />
      <canvas
        ref={canvasRef}
        style={{
          maxHeight: '100vh',
          maxWidth: '100%',
          ...canvasStyle
        }}
        {...props}
      />
    </div>
  );
};

ShapeRenderer.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  shapes: PropTypes.arrayOf(
    PropTypes.shape({
      points: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
        })
      ).isRequired,
      label: PropTypes.string,
      information: PropTypes.arrayOf(PropTypes.string),
      style: PropTypes.shape({
        strokeColor: PropTypes.string,
        lineWidth: PropTypes.number,
      }),
    })
  ),
  canvasStyle: PropTypes.object,
  className: PropTypes.string,
  onCanvasReady: PropTypes.func,
};

export default ShapeRenderer;
